import React, { useState, useEffect, FormEvent } from "react";
import { ReactComponent as LogoIcon } from "@/assets/logo/union_ver1.svg?react";
import { useNavigate, Link } from "react-router-dom";
import { checkEmailDuplication, verifyEmailToken, signupUser, SignupData } from "@/apis/user/auth";
import { BackgroundGlow, FormContainer, PageContainer } from "../LoginPage/styles";
import { useModal } from "store/modalStore";
import {
  checkEmail,
  checkPassword,
  checkRepeatPassword,
  checkUsername,
} from "@/utils/userValidation";
import { NextButton, PrevButton, ButtonContainer } from "./styles";

import EmailStep from "features/signup/EmailStep";
import PasswordStep from "features/signup/PasswordStep";
import ProfileStep from "features/signup/ProfileStep";
import { KoHeadline } from "styles/sharedStyles";
import { 
  INDUSTRY_NAMES, 
  POSITION_NAMES, 
  ROLE_NAMES, 
  COMPANY_SIZE_NAMES,
  getIndustryCodeByName,
  getPositionCodeByName,
  getRoleCodeByName,
  getCompanySizeCodeByName
} from "constants/dropdownOptions";

// === 코드 매핑 함수 (공통 함수 사용) ===
const getIndustryCode = (industry: Industry): string => {
  return getIndustryCodeByName(industry!);
};
const getCompanySizeCode = (size: CompanySize): string => {
  return getCompanySizeCodeByName(size!);
};
const getPositionCode = (position: Position): string => {
  return getPositionCodeByName(position!);
};
const getRoleCode = (role: Role): string => {
  return getRoleCodeByName(role!);
};

// === 타입 정의 (공통 상수 사용) ===
export type Industry = typeof INDUSTRY_NAMES[number] | null;
export type CompanySize = typeof COMPANY_SIZE_NAMES[number] | null;
export type Position = typeof POSITION_NAMES[number] | null;
export type Role = typeof ROLE_NAMES[number] | null;

export interface FormErrors {
  username: string | null;
  email: string | null;
  verificationCode: string | null;
  password: string | null;
  repeatPassword: string | null;
  industry: string | null;
  companySize: string | null;
  position: string | null;
  role: string | null;
  industryCustom: string | null;
  companySizeCustom: string | null;
  positionCustom: string | null;
  roleCustom: string | null;
}

const STORAGE_KEY = "signupForm";

const SignupPage: React.FC = () => {
  // === 기본 상태 ===
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [emailToken, setEmailToken] = useState("");
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  const [industry, setIndustry] = useState<Industry>(null);
  const [companySize, setCompanySize] = useState<CompanySize>(null);
  const [position, setPosition] = useState<Position>(null);
  const [role, setRole] = useState<Role>(null);
  const [companyName, setCompanyName] = useState("");

  const [industryCustom, setIndustryCustom] = useState("");
  const [companySizeCustom, setCompanySizeCustom] = useState("");
  const [positionCustom, setPositionCustom] = useState("");
  const [roleCustom, setRoleCustom] = useState("");

  const [isPrivacyConsent, setIsPrivacyConsent] = useState(false);
  const [isMarketingConsent, setIsMarketingConsent] = useState(false);

  const [codeSendCount, setCodeSendCount] = useState(0);
  const [errors, setErrors] = useState<FormErrors>({
    username: null,
    email: null,
    verificationCode: null,
    password: null,
    repeatPassword: null,
    industry: null,
    companySize: null,
    position: null,
    role: null,
    industryCustom: null,
    companySizeCustom: null,
    positionCustom: null,
    roleCustom: null,
  });

  const navigate = useNavigate();
  const { openInfo } = useModal();

  // === ✅ 1. sessionStorage에서 복원 ===
  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setStep(data.step || 1);
        setUsername(data.username || "");
        setEmail(data.email || "");
        setVerificationCode(data.verificationCode || "");
        setIndustry(data.industry || null);
        setCompanySize(data.companySize || null);
        setPosition(data.position || null);
        setRole(data.role || null);
        setCompanyName(data.companyName || "");
        setIndustryCustom(data.industryCustom || "");
        setCompanySizeCustom(data.companySizeCustom || "");
        setPositionCustom(data.positionCustom || "");
        setRoleCustom(data.roleCustom || "");
        setIsPrivacyConsent(data.isPrivacyConsent || false);
        setIsMarketingConsent(data.isMarketingConsent || false);
        setIsEmailVerified(data.isEmailVerified || false);
        setCodeSendCount(data.codeSendCount || 0);

        // **보안 정책(옵션①)**: 비밀번호는 절대 복원하지 않음.
        // 따라서 새로고침 시에는 항상 step을 1로 되돌립니다.
        setStep(1);      } catch {
        console.warn("failed to parse signup form cache");
      }
    }
  }, []);

  // === ✅ 2. sessionStorage 자동 저장 ===
  useEffect(() => {
    const dataToSave = {
      step,
      username,
      email,
      verificationCode,
      industry,
      companySize,
      position,
      role,
      companyName,
      industryCustom,
      companySizeCustom,
      positionCustom,
      roleCustom,
      isPrivacyConsent,
      isMarketingConsent,
      isEmailVerified,
      codeSendCount,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
  }, [
    step,
    username,
    email,
    verificationCode,
    industry,
    companySize,
    position,
    role,
    companyName,
    industryCustom,
    companySizeCustom,
    positionCustom,
    roleCustom,
    isPrivacyConsent,
    isMarketingConsent,
    isEmailVerified,
    codeSendCount,
  ]);

  // 2️⃣ 페이지 떠날 때(sessionStorage 삭제)
  useEffect(() => {
    return () => {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // === 회원가입 완료 시 sessionStorage 초기화 ===
  const clearSavedData = () => sessionStorage.removeItem(STORAGE_KEY);

  const handleSendVerificationCode = async () => {
    // 이메일 형식 유효성 검사
    const emailError = checkEmail(email);
    if (emailError) {
      setErrors((prev) => ({ ...prev, email: emailError }));
      return;
    }

    // 이메일이 유효하면 에러를 초기화합니다.
    setErrors((prev) => ({ ...prev, email: null }));

    try {
      // 이메일 중복 체크
      const response = await checkEmailDuplication(email);
      
      if (response.status === 200) {
        openInfo({
          title: "인증코드 전송",
          message: "이메일로 인증코드가 전송되었습니다."
        });
        setCodeSendCount((prev) => prev + 1);
      }
      else if (response.status === 400) {
        setErrors((prev) => ({
          ...prev,
          email: "이미 존재하는 이메일 주소입니다.",
        }));
        return;
      }
      else {
        openInfo({
          title: "인증코드 전송 실패",
          message: "이메일로 인증코드가 전송되지 않았습니다."
        });
      }
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        email: "이메일 확인 중 오류가 발생했습니다.",
      }));
      return;
    }
  };

  const handleVerifyCode = async () => {
    const emailError = checkEmail(email);
    if (emailError) {
      setErrors((prev) => ({ ...prev, email: emailError }));
      return false;
    }

    if (!verificationCode.trim()) {
      setErrors((prev) => ({ ...prev, verificationCode: "인증코드를 입력해주세요." }));
      return false;
    }

    try {
      const response = await verifyEmailToken(email, verificationCode, "signup");
      if (response.message === "success") {
        setErrors((prev) => ({ ...prev, verificationCode: null }));
        // 토큰 저장 (response.data.token에서 가져옴)
        if (response.data?.token) {
          setEmailToken(response.data.token);
        }
        setIsEmailVerified(true);
        openInfo({
          title: "이메일 인증 완료",
          message: "아래에 비밀번호를 입력해주세요."
        });
        return true;
      } else {
        setErrors((prev) => ({
          ...prev,
          verificationCode: "인증코드가 올바르지 않습니다.",
        }));
        return false;
      }
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        verificationCode: "인증코드 확인 중 오류가 발생했습니다.",
      }));
      return false;
    }
  };

  const handleNext = async () => {
    let hasError = false;
    if (step === 1) {
      // step 1에서는 이메일 인증과 비밀번호 입력 검증
      if (!isEmailVerified) {
        openInfo({
          title: "이메일 인증 필요",
          message: "먼저 이메일 인증을 완료해주세요."
        });
        hasError = true;
      } else {
        // 비밀번호 검증
        const usernameError = checkUsername(username);
        const passwordError = checkPassword(password);
        const repeatPasswordError = checkRepeatPassword(password, repeatPassword);
        
        if (usernameError || passwordError || repeatPasswordError) {
          setErrors((prev) => ({
            ...prev,
            username: usernameError,
            password: passwordError,
            repeatPassword: repeatPasswordError,
          }));
          hasError = true;
        }
      }
    } else if (step === 2) {
      // Step 2: Profile 검증 (업종, 회사규모 필수)
      const profileErrors: FormErrors = {
        username: null,
        email: null,
        verificationCode: null,
        password: null,
        repeatPassword: null,
        industry: null,
        companySize: null,
        position: null,
        role: null,
        industryCustom: null,
        companySizeCustom: null,
        positionCustom: null,
        roleCustom: null,
      };

      // 업종 검증 (필수)
      if (!industry) {
        profileErrors.industry = "업종을 선택해주세요.";
        hasError = true;
      } else if (industry === "기타" && (!industryCustom || industryCustom.trim() === "")) {
        profileErrors.industryCustom = "업종을 직접 입력해주세요.";
        hasError = true;
      } else if (industry === "기타" && industryCustom.trim().length > 30) {
        profileErrors.industryCustom = "업종은 30자 이내로 입력해주세요.";
        hasError = true;
      }

      // 회사규모 검증 (필수)
      if (!companySize) {
        profileErrors.companySize = "회사규모를 선택해주세요.";
        hasError = true;
      }

      // 직책 검증 (선택이지만 "기타" 선택시 입력 필요)
      if (position === "기타" && (!positionCustom || positionCustom.trim() === "")) {
        profileErrors.positionCustom = "직책을 직접 입력해주세요.";
        hasError = true;
      } else if (position === "기타" && positionCustom.trim().length > 30) {
        profileErrors.positionCustom = "직책은 30자 이내로 입력해주세요.";
        hasError = true;
      }

      // 직무 검증 (선택이지만 "기타" 선택시 입력 필요)
      if (role === "기타" && (!roleCustom || roleCustom.trim() === "")) {
        profileErrors.roleCustom = "직무를 직접 입력해주세요.";
        hasError = true;
      } else if (role === "기타" && roleCustom.trim().length > 30) {
        profileErrors.roleCustom = "직무는 30자 이내로 입력해주세요.";
        hasError = true;
      }

      // 개인정보 처리방침 동의 검증 (필수)
      if (!isPrivacyConsent) {
        openInfo({
          title: "필수 동의",
          message: "개인정보 처리방침 동의는 필수입니다."
        });
        hasError = true;
      }

      setErrors(profileErrors);
    }

    if (!hasError) {
      setStep((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    setStep((prev) => prev - 1);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
  
    if (!industry || !companySize) {
      openInfo({
        title: "필수 정보 입력",
        message: "업종과 회사규모는 필수입니다."
      });
      return;
    }
  
    if (!isPrivacyConsent) {
      openInfo({
        title: "필수 동의",
        message: "개인정보 처리방침 동의는 필수입니다."
      });
      return;
    }
  
    const signupData: SignupData = {
      id: { email, token: emailToken },
      profile: { name: username },
      password,
      company: {
        name: companyName,
        industry: { code: getIndustryCode(industry), other: industry === "기타" ? industryCustom : null },
        size: { code: getCompanySizeCode(companySize), other: null },
        jobPosition: { code: position ? getPositionCode(position) : "other", other: position === "기타" ? positionCustom : null },
        jobDuties: { code: role ? getRoleCode(role) : "other", other: role === "기타" ? roleCustom : null },
      },
      consent: { isPrivacy: isPrivacyConsent, isMarketing: isMarketingConsent },
    };
  
    try {
      const response = await signupUser(signupData);
  
      if (response.status === 200) {
        // ✅ 회원가입 성공 → 저장된 폼 데이터 초기화
        clearSavedData();
  
        setErrors({
          username: null,
          email: null,
          verificationCode: null,
          password: null,
          repeatPassword: null,
          industry: null,
          companySize: null,
          position: null,
          role: null,
          industryCustom: null,
          companySizeCustom: null,
          positionCustom: null,
          roleCustom: null,
        });
  
        navigate("/login", {
          state: {
            showAlert: true,
            message: "회원가입이 성공적으로 완료되었습니다.",
          },
          replace: true,
        });
      } else if (response.status === 400) {
        openInfo({
          title: "회원가입 실패",
          message: "이미 존재하는 이메일 주소입니다."
        });
      } else {
        openInfo({
          title: "회원가입 실패",
          message: "회원가입에 실패했습니다."
        });
      }
    } catch (error) {
      openInfo({
        title: "회원가입 실패",
        message: "서버 오류가 발생했습니다."
      });
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <>
            <EmailStep
              email={email}
              setEmail={setEmail}
              verificationCode={verificationCode}
              setVerificationCode={setVerificationCode}
              error={errors.email}
              verificationCodeError={errors.verificationCode}
              onSendCode={handleSendVerificationCode}
              codeSendCount={codeSendCount}
              isEmailDisabled={codeSendCount > 0 || isEmailVerified}
              onVerifyCode={handleVerifyCode}
              isEmailVerified={isEmailVerified}
            />
            {/* 이메일 인증 완료 후 비밀번호 입력 필드들 */}
            {isEmailVerified && (
                  <PasswordStep
                    username={username}
                    setUsername={setUsername}
                    password={password}
                    setPassword={setPassword}
                    repeatPassword={repeatPassword}
                    setRepeatPassword={setRepeatPassword}
                    errors={errors}
                  />
            )}
          </>
        );
      case 2:
        return (
          <ProfileStep
            industry={industry}
            setIndustry={setIndustry}
            companySize={companySize}
            setCompanySize={setCompanySize}
            position={position}
            setPosition={setPosition}
            role={role}
            setRole={setRole}
            industryCustom={industryCustom}
            setIndustryCustom={setIndustryCustom}
            companySizeCustom={companySizeCustom}
            setCompanySizeCustom={setCompanySizeCustom}
            positionCustom={positionCustom}
            setPositionCustom={setPositionCustom}
            roleCustom={roleCustom}
            setRoleCustom={setRoleCustom}
            companyName={companyName}
            setCompanyName={setCompanyName}
            isPrivacyConsent={isPrivacyConsent}
            setIsPrivacyConsent={setIsPrivacyConsent}
            isMarketingConsent={isMarketingConsent}
            setIsMarketingConsent={setIsMarketingConsent}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <PageContainer>
      <BackgroundGlow />
      <FormContainer onSubmit={handleSubmit} noValidate>
        <Link to="/">
          <LogoIcon style={{ width: "99px", height: "25px" }} />
        </Link>
        <KoHeadline style={{ fontWeight: 600, marginBottom: "24px" }}>회원가입</KoHeadline>
        {renderStepContent()}
        {step >= 1 && (
          <ButtonContainer>
            {step > 1 && (
              <PrevButton type="button" onClick={handlePrev}>이전</PrevButton>
            )}
            {step < 2 && (
              <NextButton type="button" onClick={handleNext}>다음</NextButton>
            )}
            {step === 2 && (
              <NextButton type="submit">회원가입</NextButton>
            )}
          </ButtonContainer>
        )}
      </FormContainer>
    </PageContainer>
  );
};

export default SignupPage;
